import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import useAuth from 'src/context/AuthContext';
import { APIService } from 'src/api/api';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import STATE_LIST from 'src/utils/stateList.json';
import { getUserRole, isAdminUser } from 'src/utils/getInitials';
import { updateCompanyBranding } from 'src/redux/settingSlice';

const ProfileFieldIcon = ({ children }) => (
  <InputAdornment position="start">
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: 1.5,
        bgcolor: '#F3F6FB',
        color: '#64748B'
      }}
    >
      {children}
    </Box>
  </InputAdornment>
);

ProfileFieldIcon.propTypes = {
  children: PropTypes.node.isRequired
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 64,
    borderRadius: 2,
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#D8E2F0'
    },
    '&:hover fieldset': {
      borderColor: '#A9BBD6'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
      boxShadow: '0 0 0 3px rgba(42, 99, 218, 0.10)'
    },
    '&.Mui-disabled': {
      bgcolor: '#F7F9FC'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#64748B'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'primary.main'
  }
};

const AccountProfileDetails = ({
  dialog = false, formId, onSavingChange, ...props
}) => {
  const dispatch = useDispatch();
  const { getUser, updateUser } = useAuth();
  const user = getUser();
  const { appSettings } = useSelector((state) => state.settings);
  const canEditCompany = isAdminUser(getUser);
  const [values, setValues] = useState({ ...user });
  const [companyName, setCompanyName] = useState(appSettings?.company_name || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues({ ...user });
  }, [user?.first_name, user?.last_name, user?.city, user?.state, user?.email, user?.mobile_num, user?.address]);

  useEffect(() => {
    setCompanyName(appSettings?.company_name || '');
  }, [appSettings?.company_name]);

  const handleChange = (event) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    onSavingChange?.(true);
    try {
      const res = await APIService.updateCurrentProfile({
        first_name: values.first_name,
        last_name: values.last_name,
        mobile_num: values.mobile_num,
        city: values.city,
        state: values.state
      });
      if (!res?.success) throw res;
      updateUser(res.result);
      setValues((currentValues) => ({ ...currentValues, ...res.result }));

      if (canEditCompany && companyName.trim()) {
        const brandingData = new FormData();
        brandingData.append('name', companyName.trim());
        await dispatch(updateCompanyBranding(brandingData)).unwrap();
      }

      dispatch(setSuccessInfo({ successMsg: 'Profile details updated successfully.' }));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.error_message || error?.detail || 'Unable to update profile details.'
      }));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <form
      autoComplete="off"
      noValidate
      onSubmit={handleSubmit}
      id={formId}
      {...props}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: dialog ? 'none' : 'calc(100vh - 140px)',
          border: dialog ? 'none' : undefined,
          boxShadow: dialog ? 'none' : undefined
        }}
      >
        <CardHeader
          avatar={dialog ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: '#EAF1FF',
                color: 'primary.main'
              }}
            >
              <PersonOutlineIcon />
            </Box>
          ) : undefined}
          sx={{
            position: dialog ? 'static' : 'sticky',
            top: dialog ? 'auto' : 0,
            zIndex: 3,
            bgcolor: 'background.paper',
            borderBottom: dialog ? 'none' : '1px solid',
            borderColor: 'divider',
            px: dialog ? 0 : 2,
            pt: dialog ? 0 : 2,
            pb: dialog ? 1 : 2
          }}
          subheader="Keep your profile information updated"
          title="Profile Details"
        />
        {!dialog && <Divider />}
        <CardContent sx={{ flex: 1, overflowY: dialog ? 'visible' : 'auto', px: dialog ? 0 : undefined }}>
          <Grid
            container
            spacing={dialog ? 2.5 : 3}
            sx={{ py: dialog ? 1.5 : 2 }}
          >
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="First name"
                name="first_name"
                onChange={handleChange}
                required
                value={values.first_name}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><PersonOutlineIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="Last name"
                name="last_name"
                onChange={handleChange}
                required
                value={values.last_name}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><PersonOutlineIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                onChange={handleChange}
                required
                disabled
                value={values.email}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><EmailOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="Phone Number"
                name="mobile_num"
                onChange={handleChange}
                required
                value={values.mobile_num}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><PhoneOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="City"
                name="city"
                onChange={handleChange}
                required
                value={values.city}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><LocationOnOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="Select State"
                name="state"
                onChange={handleChange}
                required
                select
                SelectProps={{ native: true }}
                value={values.state}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><MapOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              >
                {STATE_LIST.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                  >
                    {option.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                disabled
                label="Address"
                name="address"
                value={values.address || ''}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><HomeOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
            {canEditCompany && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: { xs: 1, md: 1.5 },
                    mb: 0.5,
                    pt: { xs: 1.5, md: 2 },
                    borderTop: '1px solid #E8EEF7'
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: '#EAF1FF',
                      color: 'primary.main'
                    }}
                  >
                    <BusinessOutlinedIcon />
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 800, color: '#10213D' }}>Company Information</Box>
                    <Box sx={{ color: '#64748B', fontSize: 14 }}>Your organization details</Box>
                  </Box>
                </Box>
              </Grid>
            )}
            {canEditCompany && (
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Company name"
                  name="company_name"
                  onChange={(event) => setCompanyName(event.target.value)}
                  required
                  value={companyName}
                  variant="outlined"
                  InputProps={{ startAdornment: <ProfileFieldIcon><BusinessOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                  sx={dialog ? fieldSx : undefined}
                />
              </Grid>
            )}
            <Grid item md={canEditCompany ? 6 : 12} xs={12}>
              <TextField
                fullWidth
                disabled
                label="Role"
                name="role"
                value={getUserRole(values.role)}
                variant="outlined"
                InputProps={{ startAdornment: <ProfileFieldIcon><BadgeOutlinedIcon fontSize="small" /></ProfileFieldIcon> }}
                sx={dialog ? fieldSx : undefined}
              />
            </Grid>
          </Grid>
        </CardContent>
        {!dialog && (
          <>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={saving}
              >
                {saving ? <CircularProgress size={22} color="inherit" /> : 'Save details'}
              </Button>
            </Box>
          </>
        )}
      </Card>
    </form>
  );
};

AccountProfileDetails.propTypes = {
  dialog: PropTypes.bool,
  formId: PropTypes.string,
  onSavingChange: PropTypes.func
};

export default AccountProfileDetails;
